import { TrashIcon } from "@images/svgIcons/trashIcon"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { useMySpaceNavigate } from "@modules/navigation/useMySpaceNavigate"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleDetailsTabs } from "@modules/programBundle/components/programBundleInfoResume"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundle } from "@modules/programBundle/hooks/useProgramBundle"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useProgramBundleAthleteSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { ProgramVisibility } from "@modules/programs/programTypes"
import { getDisplayVisibility, getSubscriptionCountText } from "@modules/programs/programUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { EditIcon } from "@modules/ui/icons/editIcon"
import { BoxesIcon, DotIcon } from "lucide-react"
import { MenuItem } from "primereact/menuitem"
import { TabMenu } from "primereact/tabmenu"
import { useMemo } from "react"
import styled, { useTheme } from "styled-components"

export const ProgramBundleInfoHeader: React.FC<{
  programBundle: ProgramBundleEntity
  editionAllowed: boolean
  activeIndex?: ProgramBundleDetailsTabs
  setActiveIndex?: (arg: ProgramBundleDetailsTabs) => void
}> = ({ programBundle, editionAllowed, activeIndex, setActiveIndex }) => {
  const { sidebarNavigateBack } = useSidebar()
  const toast = useToast()
  const theme = useTheme()
  const { isAuthor } = useProgramBundleRights(programBundle)
  const { navigateToProgramBundleForm } = useProgramBundleSidebar()
  const navigate = useMySpaceNavigate()
  const { deleteProgramBundle, isPending } = useProgramBundle(programBundle.id)
  const { programBundleStripeSubscriptionInfos } = useProgramBundleAthleteSubscription(programBundle)

  const canUserChatWithMembers =
    (isAuthor || programBundleStripeSubscriptionInfos?.canSubscriberAccessToChat()) &&
    programBundle.type !== ProgramBundleType.ON_OFF

  const openChatWithProgramGroup = () => {
    if (canUserChatWithMembers) {
      if (programBundle.id) {
        navigate(Path.Chat.Root, { state: { programToChat: programBundle.id } })
      }
    }
  }

  const tabs: MenuItem[] = useMemo(
    () =>
      [
        { label: word("programBundle.tabs.details") },
        { label: word("programBundle.tabs.programs") },
        {
          label:
            programBundle.type === ProgramBundleType.ON_OFF
              ? word("programBundle.tabs.athletes")
              : word("programBundle.tabs.subscribers"),
        },
      ].filter((_, index) => index < 2 || isAuthor),
    [isAuthor, programBundle.type],
  )

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: word("sidebar.button.dynamicLink"),
          icon: <span className="pi pi-upload" color={theme.neutralColor900} />,
          command: () => {
            if (programBundle.invitationLink) {
              navigator.clipboard.writeText(programBundle.invitationLink)
              toast.show({ severity: "info", detail: word("program.details.share.success") })
            }
          },
          disabled:
            !programBundle.invitationLink || (programBundle.visibility === ProgramVisibility.PRIVATE && !isAuthor),
        },
        {
          label: word("global.edit"),
          icon: <EditIcon color={theme.neutralColor900} />,
          command: () => {
            navigateToProgramBundleForm({
              programBundleFormProps: { programBundle },
            })
          },
          disabled: !editionAllowed || !isAuthor,
        },
        {
          label: word("global.delete"),
          icon: <TrashIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => {
            try {
              deleteProgramBundle().then(() => {
                toast.show({
                  severity: "success",
                  summary: word("programBundle.info.delete.success"),
                })
                sidebarNavigateBack()
              })
            } catch (e) {
              toast.show({
                severity: "error",
                summary: word("programBundle.info.delete.error"),
              })
            }
          },
          disabled: programBundle.type === ProgramBundleType.ON_OFF || isPending || !editionAllowed || !isAuthor,
        },
      ].filter((item) => !item.disabled),
    },
  ]

  return (
    <div className="relative">
      <div className="flex align-items-center" style={{ margin: "-85px -20px 0 -20px" }}>
        <div
          className="mb-5 flex flex-row justify-content-center align-items-start overflow-hidden rounded-33 border-noround-top w-full relative border-0"
          style={{ height: 230 }}
        >
          <div className="pt-5 pb-4 pl-4 flex flex-column justify-content-center h-full w-6">
            <HUText fontStyle="HS" color={theme.neutralColor900} numberOfLine={2} className="text-center">
              {programBundle.name}
            </HUText>

            <div className="w-full flex align-items-center gap-1 justify-content-center">
              <BoxesIcon size={12} />
              <HUText color={theme.neutralColor900} fontStyle="BS">
                {`${word("programBundle.info.labelShort")} x${programBundle.nbPrograms} (${getDisplayVisibility(programBundle.visibility)})`}
              </HUText>
              <DotIcon width={14} />
              <HUText color={theme.neutralColor900} fontStyle="BS">
                {`${getSubscriptionCountText(
                  programBundle.subscriptionCount,
                  isAuthor,
                  false,
                  true,
                  programBundle.type === ProgramBundleType.ON_OFF ? ProgramOnOffTypeOf : ProgramInfiniteTypeOf,
                ).toLowerCase()}`}
              </HUText>
            </div>
          </div>

          <div
            className="flex gap-2 w-6 h-full bg-no-repeat bg-cover bg-center border-100 relative"
            style={{
              backgroundImage: `url(${programBundle.image?.path})`,
              clipPath: "polygon(50px 0px, 100% 0px, 100% 100%, 0px 100%, 0px 230px)",
            }}
          ></div>
        </div>
      </div>

      <div className="absolute right-0 top-0">
        {ellipsisItems.length > 0 && ellipsisItems[0].items && ellipsisItems[0].items.length > 0 && (
          <EllipsisMenu items={ellipsisItems} buttonClassName="mr-1 mt-2 bg-white" style={{ width: 33, height: 33 }} />
        )}

        {canUserChatWithMembers && (
          <HUButton
            type="Rounded"
            colorType="Quaternary"
            size="M"
            className="mt-2 bg-white"
            style={{ width: 33, height: 33 }}
            onClick={openChatWithProgramGroup}
            icon={{
              iconView: <span className="pi pi-comments" style={{ color: theme.buttonQuaternaryTextColor }} />,
            }}
            title={word("chat.button.goToProgramChat")}
          />
        )}
      </div>

      {!!setActiveIndex && (
        <MenuContainer model={tabs} activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} />
      )}
    </div>
  )
}

const MenuContainer = styled(TabMenu)`
  background-color: ${({ theme }) => theme.neutralColor100};
  position: absolute;
  bottom: -12px;
  width: 100%;
  border: 2px solid ${({ theme }) => theme.neutralColor100};
  border-radius: 50px;

  .p-tabmenu-nav {
    background-color: ${({ theme }) => theme.neutralColor100};
    border-radius: 50px;
    display: flex;
    border: none;
  }

  .p-tabmenuitem {
    flex: 1;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
  }

  .p-tabmenu-ink-bar  {
    display: none;
  }

  .p-tabmenu-nav .p-tabmenuitem .p-menuitem-link {
    background-color: ${({ theme }) => theme.neutralColor100};
    color: ${({ theme }) => theme.neutralColor900};
    border-radius: 50px;
    border: none;
    justify-content: center;
    font-weight: 400;
    height: 100%;
    padding: 0px;
    width: -webkit-fill-available;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  .p-tabmenu-nav .p-tabmenuitem.p-highlight .p-menuitem-link {
    border-radius: 50px;
    font-weight: 600;
    border: none;
    background-color: ${({ theme }) => theme.neutralColor900};
    color: ${({ theme }) => theme.mainWhite};
    width: 100%;
  }

  .p-tabmenu-nav .p-tabmenuitem:not(.p-highlight):not(.p-disabled):hover .p-menuitem-link {
    background-color: ${({ theme }) => theme.neutralColor100};
    color: ${({ theme }) => theme.neutralColor900};
    border-radius: 50px;
    border: none;
    justify-content: center;
  }
`
